import { Component, OnInit, inject, input } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { IAbmCompanySegment } from 'src/app/modules/account/abm/models';
import { AudienceStatus, AudienceConfig } from '../../models/audiences.models';
import { AccountGoal } from '../../models/models';
import { TrackedPair, TrackedPairsConfig, TrackedPairsSourceType } from '../../models/tracked-pairs.models';
import { AccountEntitiesService } from '../../services/account-entities.service';
import { ModalsService } from '../../services/modals.service';
import { TrackedPairsService } from '../../services/tracked-pairs.service';

@Component({
  selector: 'app-tracked-pairs-edit',
  templateUrl: './tracked-pairs-edit.component.html',
  styleUrl: './tracked-pairs-edit.component.scss',
  standalone: false,
})
export class TrackedPairsEditComponent implements OnInit {
  private readonly trackedPairsService = inject(TrackedPairsService);
  private readonly accountEntitiesService = inject(AccountEntitiesService);
  private readonly modals = inject(ModalsService);

  public readonly accountId = input.required<number>();

  public trackedPairs: TrackedPair[] = [];
  public audiences: AudienceConfig[] = [];
  public segments: IAbmCompanySegment[] = [];
  public goals: AccountGoal[] = [];
  public isLoading = false;
  public isAdding = false;
  public loadError: string | null = null;
  public deletingIds = new Set<number>();
  public selectedSourceType: TrackedPairsSourceType = TrackedPairsSourceType.Audience;
  public selectedAudienceId: number | null = null;
  public selectedSegmentId: number | null = null;
  public selectedGoalId: number | null = null;

  public readonly sourceTypeOptions = [
    { value: TrackedPairsSourceType.Audience, label: 'Audience' },
    { value: TrackedPairsSourceType.Segment, label: 'Segment' },
  ];

  public readonly TrackedPairsSourceType = TrackedPairsSourceType;

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  get canAdd(): boolean {
    return !!this.currentSourceId && !!this.selectedGoalId && !this.isAdding;
  }

  public retryLoadData(): void {
    void this.loadData();
  }

  public onSourceTypeChange(): void {
    this.selectedAudienceId = null;
    this.selectedSegmentId = null;
  }

  public getSourceTypeLabel(sourceType: TrackedPairsSourceType): string {
    return sourceType === TrackedPairsSourceType.Audience ? 'Audience' : 'Segment';
  }

  public getSourceDisplayName(pair: TrackedPair): string {
    if (pair.sourceType === TrackedPairsSourceType.Audience) {
      const audience = this.audiences.find((item) => item.id === pair.sourceId);
      return audience?.name || `Deleted audience (#${pair.sourceId})`;
    }

    const segment = this.segments.find((item) => item.segmentId === pair.sourceId);
    return segment?.name || `Deleted segment (#${pair.sourceId})`;
  }

  public getGoalDisplayName(goalId: number): string {
    const goal = this.goals.find((item) => item.id === goalId);
    return goal?.name || `Deleted goal (#${goalId})`;
  }

  public isSourceMissing(pair: TrackedPair): boolean {
    if (pair.sourceType === TrackedPairsSourceType.Audience) {
      return !this.audiences.some((item) => item.id === pair.sourceId);
    }

    return !this.segments.some((item) => item.segmentId === pair.sourceId);
  }

  public isGoalMissing(goalId: number): boolean {
    return !this.goals.some((item) => item.id === goalId);
  }

  public async addTrackedPair(): Promise<void> {
    if (!this.canAdd) {
      return;
    }

    const request = this.buildTrackedPairRequest();
    if (!request) {
      return;
    }

    if (this.hasDuplicate(request)) {
      await this.modals.Alert('This tracked pair already exists.', 'Tracked Pairs');
      return;
    }

    try {
      this.isAdding = true;
      const id = await this.trackedPairsService.addTrackedPair(this.accountId(), request);
      this.trackedPairs = [{ id, ...request }, ...this.trackedPairs];
      this.resetForm();
    } catch (error: any) {
      if (error?.status === 409) {
        await this.modals.Alert('This tracked pair already exists.', 'Tracked Pairs');
        return;
      }

      await this.modals.Alert('Failed to add tracked pair.', 'Tracked Pairs');
    } finally {
      this.isAdding = false;
    }
  }

  public async deleteTrackedPair(pair: TrackedPair): Promise<void> {
    if (this.deletingIds.has(pair.id)) {
      return;
    }

    const isDelete = await this.modals.ConfirmDelete(
      `${this.getSourceTypeLabel(pair.sourceType)} <b>${this.getSourceDisplayName(pair)}</b> and goal <b>${this.getGoalDisplayName(pair.goalId)}</b> will be deleted. Continue?`
    );

    if (!isDelete) {
      return;
    }

    this.deletingIds.add(pair.id);

    try {
      await this.trackedPairsService.deleteTrackedPair(this.accountId(), pair.id);
      this.trackedPairs = this.trackedPairs.filter((item) => item.id !== pair.id);
    } catch {
      await this.modals.Alert('Failed to delete tracked pair.', 'Tracked Pairs');
    } finally {
      this.deletingIds.delete(pair.id);
    }
  }

  private get currentSourceId(): number | null {
    return this.selectedSourceType === TrackedPairsSourceType.Audience
      ? this.selectedAudienceId
      : this.selectedSegmentId;
  }

  private async loadData(): Promise<void> {
    try {
      this.isLoading = true;
      this.loadError = null;

      const accountId = this.accountId();
      const [trackedPairs, audiences, segments, goalsRes] = await Promise.all([
        this.trackedPairsService.getTrackedPairs(accountId),
        lastValueFrom(this.accountEntitiesService.getAccountAudiences(AudienceStatus.active, accountId)),
        this.accountEntitiesService.getAccountCompanySegments(accountId),
        this.accountEntitiesService.getAccountActiveGoals('Goal', accountId),
      ]);

      this.trackedPairs = trackedPairs;
      this.audiences = audiences.sort((a, b) => a.name.localeCompare(b.name));
      this.segments = segments.sort((a, b) => a.name.localeCompare(b.name));
      this.goals = goalsRes.data.sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      this.loadError = 'Failed to load tracked pairs configuration.';
    } finally {
      this.isLoading = false;
    }
  }

  private buildTrackedPairRequest(): TrackedPairsConfig | null {
    const sourceId = this.currentSourceId;
    if (!sourceId || !this.selectedGoalId) {
      return null;
    }

    return {
      sourceType: this.selectedSourceType,
      sourceId,
      goalId: this.selectedGoalId,
    };
  }

  private hasDuplicate(request: TrackedPairsConfig): boolean {
    return this.trackedPairs.some((pair) =>
      pair.sourceType === request.sourceType &&
      pair.sourceId === request.sourceId &&
      pair.goalId === request.goalId
    );
  }

  private resetForm(): void {
    this.selectedAudienceId = null;
    this.selectedSegmentId = null;
    this.selectedGoalId = null;
  }
}
